import {SpineButton} from '@fantasmagames/core/src/display/SpineButton';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {AnimationNames} from '@fantasmagames/slot/src/animations/AnimationNames';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {AutoplaySlider} from '@fantasmagames/slot/src/display/Slider/AutoplaySlider';
import {Slider, SliderValueChangedCallback} from '@fantasmagames/slot/src/display/Slider/Slider';
import {SliderNumberBox} from '@fantasmagames/slot/src/display/Slider/SliderNumberBox';
import {IAutoplaySettings, IAutoplayValues} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerModule';
import {AutoplayMenuViewEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuViewEvents';
import {GenericButton} from '@fantasmagames/slot/src/modules/popupmenus/GenericButton';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';

export class AutoplayMenuView extends View {
	public static readonly LOSS_LIMIT_CHANGED: string = 'AutoplayMenuView::LOSS_LIMIT_CHANGED';
	public static readonly SINGLE_WIN_LIMIT_CHANGED: string = 'AutoplayMenuView::SINGLE_WIN_LIMIT_CHANGED';
	public static readonly WIN_LIMIT_CHANGED: string = 'AutoplayMenuView::WIN_LIMIT_CHANGED';
	public static readonly SPINS_AMOUNT_CHANGED: string = 'AutoplayMenuView::SPIN_AMOUNT_CHANGED';

	public static readonly MAX_BUTTONS_PER_ROW: number = 4;
	public static readonly Y_BUTTON_OFFSET: number = 90;

	protected _autoplayAmount: number;
	protected _lastAutoplayAmount: number;
	protected _autoplayDefault: number;

	protected _buttons: GenericButton[] = [];
	protected _spineLayout: Spine;

	protected _buttonDisabledTint: number = 0xffffff;

	protected _buttonValues: string[] | number[] = [];

	protected _cancelButton: SpineButton;
	protected _acceptButton: SpineButton;

	protected _acceptEnabled: boolean = false;

	protected _background: PIXI.Container;
	protected _titleBackground: PIXI.Container;

	protected _buttonsCallback: Function;

	protected _menuTitle: PIXI.Text;
	protected _buttonStyle: PIXI.TextStyle;
	protected _titleStyle: PIXI.TextStyle;
	protected _buttonOverStyle: PIXI.TextStyle;
	protected _buttonDownStyle: PIXI.TextStyle;
	protected _buttonDisabledStyle: PIXI.TextStyle;

	protected _limitErrorText: PIXI.Text;
	protected _limitErrorStyle: PIXI.TextStyle;

	protected _xOffset: number = 0;
	protected _yOffset: number = 0;

	protected _singleWinSlider: Slider;
	protected _winSlider: Slider;
	protected _lossSlider: Slider;

	protected _stopAutoplayAfterWinCheckbox: Spine;
	protected _stopAutoplayAfterWinChecked: boolean = false;

	protected _lossLimitSlotNames: string[] = [];
	protected _singleWinLimitSlotNames: string[] = [];
	protected _winLimitSlotNames: string[] = [];
	protected _stopConfigSlotNames: string[] = [];
	protected _untilStopSlotNames: string[] = [];

	protected _isAcceptDisabled: boolean = true;
	protected _buttonScaleMod: number = 0.85; // Magic number...

	public init(): void {
		super.init();

		this.setupTextStyles();
		this.setupBaseLayout();
		this.setupBaseButtons();
		this.setupCheckboxes();

		this._buttonsCallback = this.setAutoplay;
		this._acceptButton.disable();
		this._isAcceptDisabled = true;
		this.title = 'AUTO PLAY';
	}

	public setupOptionsVisibility(autoplayData: IAutoplaySettings): void {
		const slotsToHide: string[] = [];
		const slotsToShow: string[] = [];

		if (autoplayData.lossLimitEnabled) {
			slotsToShow.push(...this._lossLimitSlotNames);
		} else {
			slotsToHide.push(...this._lossLimitSlotNames);
		}

		if (autoplayData.winLimitEnabled) {
			slotsToShow.push(...this._singleWinLimitSlotNames);
			slotsToShow.push(...this._stopConfigSlotNames);
			slotsToShow.push(...this._winLimitSlotNames);
		} else {
			slotsToHide.push(...this._singleWinLimitSlotNames);
			slotsToHide.push(...this._stopConfigSlotNames);
			slotsToHide.push(...this._winLimitSlotNames);
		}

		if (autoplayData.untilStop) {
			slotsToShow.push(...this._untilStopSlotNames);
		} else {
			slotsToHide.push(...this._untilStopSlotNames);
		}

		for (const slotName of slotsToHide) {
			this.spineUtils.findSlotContainerByName(this._spineLayout, slotName).visible = false;
		}

		for (const slotName of slotsToShow) {
			this.spineUtils.findSlotContainerByName(this._spineLayout, slotName).visible = true;
		}
	}

	public hideAutoplaySettings(autoplayData: IAutoplaySettings): void {}

	public showAutoplaySettings(autoplayData: IAutoplaySettings): void {}

	public disableMenuDueToLossLimitExceedsBet(): void {
		this._acceptButton.disable();
		this._limitErrorText.text = this.getLimitText('loss');
		this._limitErrorText.visible = true;
	}

	public disableMenuDueToWinLimitTooLow(): void {
		this._acceptButton.disable();
		this._limitErrorText.text = this.getLimitText('win');
		this._limitErrorText.visible = true;
	}

	public disableMenuDueToSingleWinLimitTooLow(): void {
		this._acceptButton.disable();
		this._limitErrorText.text = this.getLimitText('singleWin');
		this._limitErrorText.visible = true;
	}

	public disableMenuDueToSpinAmountTooHigh(): void {
		this._acceptButton.disable();
		this._limitErrorText.text = this.getLimitText('maxSpins');

		this._limitErrorText.visible = true;
	}

	public enableMenuLimitsOK(): void {
		this._acceptButton.enable();
		this._limitErrorText.visible = false;
	}

	public enableMenuLossLimitOK(): void {
		this._acceptButton.enable();
		this._limitErrorText.visible = false;
	}

	// If we dont get any autoplaySettings we default with 100 values based on current bet.
	// The 100 and 1000 values are taken from another popular game on the market with this type of autoplay menu.
	public setSliderValues(autoplaySettings: IAutoplaySettings, value: number): void {
		if (!autoplaySettings) {
			const singleWinValues = [];
			const winValues = [];
			const lossValues = [];

			for (let i = 0; i <= 100; i++) {
				singleWinValues.push(i * value * 0.5);
				winValues.push(i * value);
				lossValues.push(i * value);
			}

			this._singleWinSlider.updateValues(singleWinValues);
			this._winSlider.updateValues(winValues);
			this._lossSlider.updateValues(lossValues);
		} else {
			this._singleWinSlider.updateValues(autoplaySettings.winLimitsAmounts, this.getNumberArrayIndex(autoplaySettings.winLimitsAmounts, autoplaySettings.winLimitDefault));
			this._winSlider.updateValues(autoplaySettings.winLimitsAmounts, this.getNumberArrayIndex(autoplaySettings.winLimitsAmounts, autoplaySettings.winLimitDefault));
			this._lossSlider.updateValues(autoplaySettings.lossLimitsAmounts, this.getNumberArrayIndex(autoplaySettings.lossLimitsAmounts, autoplaySettings.lossLimitDefault));
		}
	}

	public getCurrentAutoplayParameters(): IAutoplayValues {
		let autoplayValues: IAutoplayValues;
		autoplayValues = {
			singleWinLimit: this._singleWinSlider.currentValue,
			winLimit: this._winSlider.currentValue,
			lossLimit: this._lossSlider.currentValue,
			stopAutoplayOnWin: this._stopAutoplayAfterWinChecked,
		};

		return autoplayValues;
	}

	public resetSliders(): void {
		this._singleWinSlider.reset();
		this._winSlider.reset();
		this._lossSlider.reset();
	}

	public set title(title: string) {
		this._menuTitle.text = title;
	}

	protected getNumberArrayIndex(array: number[], valueToFind: number): number {
		return array.findIndex( (item) => {
			return item === valueToFind;
		} );
	}

	public set buttonDisabledTint(value: number) {
		this._buttonDisabledTint = value;
	}

	public set autoplayAmount(v: number) {
		this._autoplayAmount = v;
	}

	public get autoplayAmount(): number {
		return this._autoplayAmount;
	}

	public set autoplayDefault(value: number) {
		this._autoplayDefault = value;
	}

	public get autoplayDefault(): number {
		return this._autoplayDefault;
	}

	public set offset([xOffset, yOffset]: [number, number]) {
		this._xOffset = xOffset;
		this._yOffset = yOffset;
	}

	public set lastAutoplayAmount(value: number) {
		this.restoreToLastAutoplayAmount(value);
		this._lastAutoplayAmount = value;
	}

	public set buttonValues(buttonValues: string[] | number[]) {
		this._buttonValues = buttonValues;
		this.generateButtons();
	}

	public translate(): void {
		const titleText: string = this.translator.getObject('BaseStrings', 'generic', 'g_autoplay') || 'AUTO PLAY';
		this.title = titleText.toUpperCase();
		let textStyle: PIXI.TextStyle;
		const locale: string = this.resources.getLocale();

		if (locale !== 'th' && locale !== 'zh-cn' && locale !== 'zh-tw' && locale !== 'sl' && locale !== 'vi') {
			textStyle = this.textStyles.settingsDescription;
		} else {
			textStyle = this.textStyles.autoplaySettingsDescription;
		}

		const translations = [];
		translations.push(
			this.insertTexts(
				this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('STOP_AUTOPLAY_TEXT')],
				textStyle,
				'g_autoplay_stopautoplay',
				'STOP AUTOPLAY AFTER WIN',
			),
		);
		translations.push(
			this.insertTexts(
				this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('SINGLE_WIN_TEXT')],
				textStyle,
				'g_autoplay_singlewinlimit',
				'SINGLE WIN LIMIT',
			),
		);
		translations.push(
			this.insertTexts(
				this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('WIN_LIMIT_TEXT')],
				textStyle,
				'g_autoplay_winlimit',
				'WIN LIMIT',
			),
		);
		translations.push(
			this.insertTexts(
				this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('LOSS_LIMIT_TEXT')],
				textStyle,
				'g_autoplay_losslimit',
				'LOSS LIMIT',
			),
		);

		const limitErrorText: string =
			this.translator.getObject('BaseStrings', 'generic', 'g_currentbet_losslimit') || 'Current Bet Exceeds Loss Limit';

		this._limitErrorText.text = limitErrorText;

		this.updateTitleScale();

		if (locale !== 'th' && locale !== 'zh-cn' && locale !== 'zh-tw' && locale !== 'sl' && locale !== 'vi') {
			this.updateTextsScale(translations);
		}
	}

	protected preCallback(valueId: number, ...args: any[]): void {
		for (const button of this._buttons) {
			if (button.id !== valueId) {
				button.resetState();
			}
		}
	}

	protected setupTextStyles(): void {
		this._titleStyle = this.textStyles.buttonMenuTitleStyle;
		this._titleStyle.wordWrapWidth = this.resolutionManager.resolution.width;

		this._buttonStyle = this.textStyles.buttonBaseStyle;
		this._buttonOverStyle = this.textStyles.buttonOverStyle;
		this._buttonDownStyle = this.textStyles.buttonDownStyle;
		this._buttonDisabledStyle = this.textStyles.buttonDisabledStyle;

		this._limitErrorStyle = this.textStyles.limitErrorTextStyle;
	}

	protected setupBaseButtons(): void {
		const acceptPosition = this._spineLayout.skeleton.findBone('ACCEPT_BUTTON').data;
		const cancelPosition = this._spineLayout.skeleton.findBone('DECLINE_BUTTON').data;

		this._acceptButton = this.spineUtils.createSpineButton('ACCEPT_BUTTON.json', this.onAcceptPressed.bind(this));
		this._acceptButton.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
		this._acceptButton.setTransform(
			this.resolutionManager.halfResolution.width + acceptPosition.x,
			this.resolutionManager.resolution.height - (this.resolutionManager.halfResolution.height + acceptPosition.y),
		);

		this._isAcceptDisabled = true;

		this._cancelButton = this.spineUtils.createSpineButton('DECLINE_BUTTON.json', this.onCancelPressed.bind(this));
		this._cancelButton.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
		this._cancelButton.setTransform(
			this.resolutionManager.halfResolution.width + cancelPosition.x,
			this.resolutionManager.resolution.height - (this.resolutionManager.halfResolution.height + cancelPosition.y),
		);

		this.container.addChild(this._acceptButton);
		this.container.addChild(this._cancelButton);

		// TODO: Propose max position or count of Bone children in hierarchy to determine buttons per row.
		const choiceButton1Position = this._spineLayout.skeleton.findBone('BOX_CONTAINER1');
		const choiceButton2Position = this._spineLayout.skeleton.findBone('BOX_CONTAINER2');
		const choiceButton3Position = this._spineLayout.skeleton.findBone('BOX_CONTAINER3');

		// this.offset = [choiceButton1Position.x - choiceButton2Position.x, yOffset];
		this.offset = [choiceButton1Position.x - choiceButton2Position.x, choiceButton1Position.y - choiceButton3Position.y];
	}

	protected getLimitText(limitType: string): string {
		switch (limitType) {
			case 'loss':
				return this.translator.getObject('BaseStrings', 'generic', 'g_currentbet_losslimit') || 'Current Bet Exceeds Loss Limit';
			case 'win':
				return this.translator.getObject('BaseStrings', 'generic', 'g_currentbet_winlimit') || 'Current Win Limit is too low';
			case 'singleWin':
				return (
					this.translator.getObject('BaseStrings', 'generic', 'g_currentbet_singlewinlimit') ||
					'Current Single Win Limit is too Low'
				);
			case 'maxSpins':
				return (
					this.translator.getObject('BaseStrings', 'generic', 'g_currentbet_maxspinslimit') || 'Current Spin amount is too high'
				);
			default:
				return this.translator.getObject('BaseStrings', 'generic', 'g_currentbet_invalidlimit') || 'Current Settings are invalid';
		}
	}

	protected updateTitleScale(): void {
		const maxSize = {
			x: this._titleStyle.wordWrapWidth,
			y: (this._titleBackground.children[0] as PIXI.Sprite).height,
		};
		if (this._menuTitle.width > maxSize.x || this._menuTitle.height > maxSize.y) {
			GameUtils.correctSpriteScale(this._menuTitle, maxSize);
			// this._menuTitle.scale.y *= -1;
		}
	}

	protected updateTextsScale(texts: PIXI.Text[]): void {
		let maxLength = 0;
		let longestText;

		for (const text of texts) {
			if (text.text.length > maxLength) {
				maxLength = text.text.length;
				longestText = text;
			}
		}

		let maxSize = {x: 140, y: 150};
		if (this.device.isMobile) {
			maxSize = {x: 175, y: 300};
		}
		GameUtils.correctSpriteScale(longestText, maxSize);
		for (const text of texts) {
			text.scale = longestText.scale;
		}
	}

	protected setupBaseLayout(): void {
		this._spineLayout = new Spine(this.resources.getSpine('AUTOPLAY_MENU.json'));

		this._background = this.spineUtils.findSlotContainerByName(this._spineLayout, 'BACKGROUND_SOLID');
		this._titleBackground = this.spineUtils.findSlotContainerByName(this._spineLayout, 'MENU_TITLE_BACKGROUND');
		this._background.interactive = true;

		this.cleanListOfSpineChildrenFromSlotNames(this._spineLayout, [
			'BOX_CONTAINER1',
			'BOX_CONTAINER2',
			'ACCEPT_BUTTON',
			'ACCEPT_BUTTON_CONFIRM',
			'DECLINE_BUTTON',
			'DECLINE_BUTTON_EXIT',
		]);

		// Menu title adding
		this._menuTitle = new PIXI.Text('Title', this._titleStyle);
		this.spineUtils.replaceElementInSpine(this._spineLayout, 'MENU_TITLE', this._menuTitle);

		// Adding limit error text.
		this._limitErrorText = new PIXI.Text('Current Bet Exceeds Loss Limit', this._limitErrorStyle);
		this.spineUtils.replaceElementInSpine(this._spineLayout, 'LIMIT_MESSAGE', this._limitErrorText);

		////////////////////////////////////////////////////////////////////
		// Create autoplay sliders.
		////////////////////////////////////////////////////////////////////

		// createSliders
		this._singleWinSlider = this.makeNewAutoplaySlider(
			'SINGLE_WIN_SLIDER',
			this.onSingleWinLimitChanged.bind(this),
			'SINGLE_WIN_BOX_NUMBER',
		);
		this._winSlider = this.makeNewAutoplaySlider('WIN_LIMIT_SLIDER', this.onWinLimitChanged.bind(this), 'WIN_LIMIT_BOX');
		this._lossSlider = this.makeNewAutoplaySlider('LOSS_LIMIT_SLIDER', this.onLossLimitChanged.bind(this), 'LOSS_LIMIT_BOX');

		////////////////////////////////////////////////////////////////////
		// Add spinelayout, destroyPlaceholderGraphics and update once.
		////////////////////////////////////////////////////////////////////
		this.destroyAllPlaceholderGraphic(this._spineLayout);
		this.container.addChild(this._spineLayout);
		this._spineLayout.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
		this._spineLayout.update(0);

		// Anchors need to be set here or they will reset on layout update(0).
		this._menuTitle.anchor.set(0.5, 0.5);
		this._limitErrorText.anchor.set(0, 0.5);
	}

	protected cleanListOfSpineChildrenFromSlotNames(spine: Spine, slotNames: string[]): void {
		for (let item = 0; item < slotNames.length; item++) {
			const slot = slotNames[item];
			const slotContainer = this.spineUtils.findSlotContainerByName(spine, slot);
			if (slotContainer) {
				slotContainer.removeChildren();
			}
		}
	}

	protected makeNewAutoplaySlider(
		slotName: string,
		onValueChangedCallback: SliderValueChangedCallback,
		sliderNumberBoxSlot: string,
	): AutoplaySlider {
		const slider: AutoplaySlider = new AutoplaySlider(
			this.spineUtils.findSlotContainerByName(this._spineLayout, slotName),
			new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_SLIDER.png')),
			new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_SLIDER_FULL.png')),
			new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_SLIDER_DOT.png')),
			this.device.isMobile,
		);

		slider.currencyFormatter = this.currencyFormatter;
		slider.onValueChangedCallback = onValueChangedCallback;
		slider.sliderNumberBox = this.makeSliderNumberBox(sliderNumberBoxSlot);

		return slider;
	}

	protected makeSliderNumberBox(slotName: string): SliderNumberBox {
		const numberBox: SliderNumberBox = new SliderNumberBox(
			new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_MENU_BOX.png')),
			this._buttonStyle,
			this.device.isMobile,
			this.sliderNumberTextScaleMod,
		);
		this.spineUtils.replaceElementInSpine(this._spineLayout, slotName, numberBox);

		return numberBox;
	}

	protected destroyAllPlaceholderGraphic(spine: Spine): void {
		const slots = spine.slotContainers;

		slots.forEach((slot) => {
			slot.children.forEach((child) => {
				if ((child as any).region && (child as any).region.name === 'PLACEHOLDER') {
					child.destroy();
				}
			});
		});
	}

	protected onLossLimitChanged(newValue: number): void {
		this.emit(AutoplayMenuView.LOSS_LIMIT_CHANGED, newValue);
	}

	protected onWinLimitChanged(newValue: number): void {
		this.emit(AutoplayMenuView.WIN_LIMIT_CHANGED, newValue);
	}

	protected onSingleWinLimitChanged(newValue: number): void {
		this.emit(AutoplayMenuView.SINGLE_WIN_LIMIT_CHANGED, newValue);
	}

	protected insertTexts(slot: PIXI.Container, style: PIXI.TextStyle, tag: string, fallbackText?: string): PIXI.Text {
		const translation: string = this.translator.getObject('BaseStrings', 'generic', tag) || fallbackText;
		const text = new PIXI.Text(translation.toUpperCase(), style);
		text.transform.updateTransform(slot.transform);
		slot.removeChildren();
		slot.addChild(text);
		text.scale.set(1, -1);
		text.anchor.set(0, 0.5);

		return text;
	}

	protected setAutoplay(value: number, isRealClick: boolean): void {
		if (isRealClick) {
			this.soundPlayer.play('buttonClick');
		}

		this.preCallback(value);
		// this._acceptButton.enable();
		// this._isAcceptDisabled = false;
		this.autoplayAmount = value;
		this.onSpinsAmountChanged();
	}

	protected onSpinsAmountChanged(): void {
		this.emit(AutoplayMenuView.SPINS_AMOUNT_CHANGED, this.autoplayAmount);
	}

	protected emitAccepted(): void {
		this.emit(AutoplayMenuViewEvents.ACCEPTED, this._autoplayAmount);
	}

	protected emitButtonPressed(): void {
		this.emit(AutoplayMenuViewEvents.BUTTON_PRESSED);
	}

	protected emitCancelled(): void {
		this.emit(AutoplayMenuViewEvents.CANCELLED);
	}

	protected emitClosed(): void {
		this.emit(AutoplayMenuViewEvents.AUTO_PLAY_MENU_CLOSED);
	}

	protected emitOpened(): void {
		this.emit(AutoplayMenuViewEvents.AUTO_PLAY_MENU_OPENED);
	}

	protected formatText(text: string): string {
		return 'x' + text;
	}

	protected restoreToLastAutoplayAmount(value: number): void {
		const lastAutoplayAmount = this._buttons.find((button) => button.id === value);
		if (lastAutoplayAmount) {
			lastAutoplayAmount.setSelected();
			lastAutoplayAmount.forceClicked();
			this.setAutoplay(value, false);
		}
	}

	protected generateButtons(): void {
		for (const buttonValue of this._buttonValues) {
			const button = this.createButton(buttonValue);
			this._buttons.push(button);
			this.container.addChild(button);
		}

		this.layoutButtons();
	}

	protected onCancelPressed(): void {
		this.soundPlayer.play('buttonClick');

		this.emitCancelled();
		this.hide();
	}

	protected onAcceptPressed(): void {
		this.soundPlayer.play('buttonClick');

		this.emitAccepted();
		this.hide();
	}

	protected createButton(value: any): GenericButton {
		const button = new GenericButton(this._buttonsCallback.bind(this, value));
		button.id = value;

		const textValue = this.formatText(value);

		const up = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_MENU_BOX.png'));
		const down = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_MENU_BOX_CLICKED.png'));
		const over = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_MENU_BOX_HOVER.png'));
		const disabled = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'AUTOPLAY_MENU_BOX_DISABLED.png'));

		up.anchor.set(0.5, 0.5);
		down.anchor.set(0.5, 0.5);
		over.anchor.set(0.5, 0.5);
		disabled.anchor.set(0.5, 0.5);

		const textUp = new PIXI.Text(textValue, this._buttonStyle);
		textUp.anchor.set(0.5, 0.5);
		up.addChild(textUp);

		const textDown = new PIXI.Text(textValue, this._buttonDownStyle);
		textDown.anchor.set(0.5, 0.5);
		down.addChild(textDown);

		const textOver = new PIXI.Text(textValue, this._buttonOverStyle);
		textOver.anchor.set(0.5, 0.5);
		over.addChild(textOver);

		const textDisabled = new PIXI.Text(textValue, this._buttonDisabledStyle);
		textDisabled.anchor.set(0.5, 0.5);
		disabled.addChild(textDisabled);

		disabled.tint = this.buttonDisabledTint;

		button.setFrames(up, down, over, disabled);

		return button;
	}

	protected layoutButtons(): void {
		const Start = this._spineLayout.skeleton.findBone('BOX_CONTAINER1').data;

		const startPosition = {
			x: Start.x + this.resolutionManager.halfResolution.width,
			y: this.resolutionManager.halfResolution.height - Start.y,
		};

		for (let button = 0; button < this._buttons.length; button++) {
			const xPosition = this._xOffset * -(button % AutoplayMenuView.MAX_BUTTONS_PER_ROW) + startPosition.x;
			const yPosition = this._yOffset * Math.floor(button / AutoplayMenuView.MAX_BUTTONS_PER_ROW) + startPosition.y;

			this._buttons[button].position.set(xPosition, yPosition);
		}
	}

	protected setupCheckboxes(): void {
		this._stopAutoplayAfterWinCheckbox = new Spine(this.resources.getSpine('SETTINGS_CONFIG_BUTTON.json'));

		const slot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('STOP_AUTOPLAY_CONFIG')];
		slot.addChild(this._stopAutoplayAfterWinCheckbox);

		this._stopAutoplayAfterWinCheckbox.state.setAnimation(0, this.getToggleAnimation(this._stopAutoplayAfterWinChecked), false);

		this._stopAutoplayAfterWinCheckbox.update(0);
		this._stopAutoplayAfterWinCheckbox.interactive = true;
		this._stopAutoplayAfterWinCheckbox.buttonMode = true;
		this._stopAutoplayAfterWinCheckbox.on('mousedown', this.stopAutoplayAfterWinToggle.bind(this));
		this._stopAutoplayAfterWinCheckbox.on('touchstart', this.stopAutoplayAfterWinToggle.bind(this));
	}

	protected getToggleAnimation(isActive: boolean): string {
		return isActive ? AnimationNames.SETTINGS_CONFIG_TOGGLE.Name.ACTIVE : AnimationNames.SETTINGS_CONFIG_TOGGLE.Name.INACTIVE;
	}

	protected stopAutoplayAfterWinToggle(): void {
		if (this._stopAutoplayAfterWinChecked) {
			this._stopAutoplayAfterWinCheckbox.state.setAnimation(0, AnimationNames.SETTINGS_CONFIG_TOGGLE.Name.INACTIVE, true);
			this._stopAutoplayAfterWinChecked = false;
		} else {
			this._stopAutoplayAfterWinCheckbox.state.setAnimation(0, AnimationNames.SETTINGS_CONFIG_TOGGLE.Name.ACTIVE, true);
			this._stopAutoplayAfterWinChecked = true;
		}
		this._stopAutoplayAfterWinCheckbox.update(0);
	}

	protected get sliderNumberTextScaleMod(): number {
		return this._buttonScaleMod;
	}

}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuView.ts